import { INDIRECT_CASH_FLOW_OPERATING_BLOCKS } from './ledger.js';
import { cashFlowSeriesGroupRows, cashFlowSeriesReconciliationRows, mergeSeriesLines, } from './cash-flow-series-presentation.js';
/**
 * Build the indirect-method series as the shared flat row model.
 *
 * The shape follows the single-period indirect statement: operating opens on
 * net profit, then every block in `INDIRECT_CASH_FLOW_OPERATING_BLOCKS`, then
 * one subtotal spanning all of them; investing and financing follow as ordinary
 * groups; the reconciliation block closes.
 *
 * A block is rendered when it carries lines in ANY column, and is then rendered
 * in every column - matching how a line absent from one month is a real zero
 * there rather than a gap. A block empty across the whole series is omitted, as
 * the single-period statement omits an empty block.
 */
export function buildIndirectCashFlowSeriesTableRows(series) {
    const statements = series.periods;
    const rows = [];
    rows.push({ kind: 'section', label: 'Operating activities' });
    rows.push({
        kind: 'total',
        label: 'Net profit',
        amountsMinorUnits: statements.map((statement) => statement.operating.netProfitMinorUnits),
    });
    // Net profit is itself a value row the operating subtotal adds up, so the
    // span starts at one rather than zero.
    let operatingSumSpan = 1;
    for (const block of INDIRECT_CASH_FLOW_OPERATING_BLOCKS) {
        const linesByColumn = statements.map((statement) => statement.operating[block.key]);
        const lines = mergeSeriesLines(linesByColumn);
        if (lines.length === 0)
            continue;
        rows.push({ kind: 'section', label: block.label });
        rows.push(...lines.map((line) => ({ kind: 'line', line })));
        operatingSumSpan += lines.length;
    }
    rows.push({
        kind: 'subtotal',
        label: 'Total operating activities',
        amountsMinorUnits: statements.map((statement) => statement.operating.subtotalMinorUnits),
        sumSpan: operatingSumSpan,
    });
    for (const activity of ['investing', 'financing']) {
        const groups = statements.map((statement) => statement[activity]);
        const firstGroup = groups[0];
        if (!firstGroup)
            continue;
        rows.push(...cashFlowSeriesGroupRows(firstGroup.label, groups.map((group) => group.lines), groups.map((group) => group.subtotalMinorUnits)));
    }
    rows.push(...cashFlowSeriesReconciliationRows(statements));
    return rows;
}
